import { AlertTriangle, CheckCircle, Edit, Minus, Package, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import Sidebar from '../components/layout/Sidebar';
import { addInventory, getInventory, updateInventory } from '../services/inventoryService';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
  }
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const SearchInput = styled(Input)`
  max-width: 400px;
`;

const TableContainer = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${theme.colors.secondary};
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${theme.colors.border};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semiBold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.secondary};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
  position: relative;
  
  &:last-child {
    pointer-events: auto;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ status }) => {
    if (status === 'low') return `${theme.colors.status.pending}20`;
    if (status === 'out') return `${theme.colors.status.outOfStock}20`;
    return `${theme.colors.status.ready}20`;
  }};
  color: ${({ status }) => {
    if (status === 'low') return theme.colors.status.pending;
    if (status === 'out') return theme.colors.status.outOfStock;
    return theme.colors.status.ready;
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  position: relative;
  z-index: 1;
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSize.sm};
  position: relative;
  z-index: 10;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.primary};
    z-index: 11;
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
    pointer-events: none;
    flex-shrink: 0;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

const UnitSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  min-height: 44px;
  cursor: pointer;

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary}15;
  }

  &:disabled {
    background-color: ${theme.colors.secondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const UnitOption = styled.option`
  padding: ${theme.spacing.sm};
`;

// Common unit options for stock items
const unitOptions = ['kg', 'grams', 'liters', 'ml', 'packets', 'pieces', 'boxes', 'bags', 'bottles', 'cans', 'dozen', 'units'];


function getStatus(current, minimum) {
  if (current === 0) return 'out';
  if (current < minimum) return 'low';
  return 'good';
}

function InventoryManagement() {
  const [inventory, setInventory] = useState([]);
    // Fetch inventory from backend
    useEffect(() => {
      getInventory().then((data) => {
        setInventory(
          data.map((item) => ({
            ...item,
            status: getStatus(item.currentStock, item.minimumStock),
          }))
        );
      });
    }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    currentStock: '',
    unit: '',
    minimumStock: '',
  });
  const nameInputRef = useRef(null);

  const getStatus = (current, minimum) => {
    if (current === 0) return 'out';
    if (current < minimum) return 'low';
    return 'good';
  };

  const handleAddStock = () => {
    setEditingItem(null);
    setFormData({ name: '', currentStock: '', unit: '', minimumStock: '' });
    setShowModal(true);
  };

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (showModal && nameInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [showModal]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', handleEscape);
    // eslint-disable-next-line no-undef
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      currentStock: item.currentStock?.toString() || '',
      unit: item.unit || '',
      minimumStock: item.minimumStock?.toString() || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = formData.name.trim();
    const trimmedUnit = formData.unit.trim();
    if (!trimmedName) return alert('Please enter an item name');
    if (!trimmedUnit) return alert('Please select or enter a unit');
    if (formData.currentStock === '' || formData.currentStock === null || formData.currentStock === undefined) return alert('Please enter current stock value');
    const currentStock = Number.parseFloat(formData.currentStock);
    if (Number.isNaN(currentStock)) return alert('Please enter a valid number for current stock');
    if (currentStock < 0) return alert('Current stock cannot be negative');
    if (formData.minimumStock === '' || formData.minimumStock === null || formData.minimumStock === undefined) return alert('Please enter minimum stock value');
    const minimumStock = Number.parseFloat(formData.minimumStock);
    if (Number.isNaN(minimumStock)) return alert('Please enter a valid number for minimum stock');
    if (minimumStock < 0) return alert('Minimum stock cannot be negative');

    if (editingItem) {
      // Update in backend
      const updated = await updateInventory(editingItem.id, {
        name: trimmedName,
        currentStock,
        unit: trimmedUnit,
        minimumStock,
      });
      setInventory((prev) =>
        prev.map((item) =>
          item.id === editingItem.id
            ? { ...updated, status: getStatus(updated.currentStock, updated.minimumStock) }
            : item
        )
      );
    } else {
      // Add to backend
      const added = await addInventory({
        name: trimmedName,
        currentStock,
        unit: trimmedUnit,
        minimumStock,
      });
      setInventory((prev) => [
        ...prev,
        { ...added, status: getStatus(added.currentStock, added.minimumStock) },
      ]);
    }
    setShowModal(false);
    setFormData({ name: '', currentStock: '', unit: '', minimumStock: '' });
  };

  const handleAdjustStock = async (itemId, adjustment) => {
    const item = inventory.find((i) => i.id === itemId);
    if (!item) return;
    const newStock = Math.max(0, item.currentStock + adjustment);
    const updated = await updateInventory(itemId, {
      name: item.name,
      currentStock: newStock,
      unit: item.unit,
      minimumStock: item.minimumStock,
    });
    setInventory((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...updated, status: getStatus(updated.currentStock, updated.minimumStock) }
          : i
      )
    );
  };

  const filteredInventory = inventory.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>
            <Package size={32} />
            Inventory / Stock Management
          </h1>
        </Header>

        <ActionsBar>
        <SearchInput
          type="text"
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button variant="primary" onClick={handleAddStock}>
          <Plus size={20} />
          Add Stock Item
        </Button>
      </ActionsBar>

      <TableContainer>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell>Item Name</TableHeaderCell>
              <TableHeaderCell>Current Stock</TableHeaderCell>
              <TableHeaderCell>Unit</TableHeaderCell>
              <TableHeaderCell>Minimum Stock</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {filteredInventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan="6" style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
                  No items found
                </TableCell>
              </TableRow>
            ) : (
              filteredInventory.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <strong>{item.name}</strong>
                  </TableCell>
                  <TableCell>{item.currentStock}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{item.minimumStock}</TableCell>
                  <TableCell>
                    <StatusBadge status={item.status}>
                      {item.status === 'out' && <AlertTriangle size={14} />}
                      {item.status === 'low' && <AlertTriangle size={14} />}
                      {item.status === 'good' && <CheckCircle size={14} />}
                      {(() => {
                        if (item.status === 'out') return 'Out of Stock';
                        if (item.status === 'low') return 'Low Stock';
                        return 'In Stock';
                      })()}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <ActionButton 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEdit(item);
                        }}
                      >
                        <Edit />
                        Edit
                      </ActionButton>
                      <ActionButton 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAdjustStock(item.id, 10);
                        }}
                      >
                        <Plus size={14} />
                        +10
                      </ActionButton>
                      <ActionButton 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAdjustStock(item.id, -10);
                        }}
                      >
                        <Minus size={14} />
                        -10
                      </ActionButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{editingItem ? 'Edit Stock Item' : 'Add Stock Item'}</h2>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalForm onSubmit={handleSubmit}>
              <InputGroup>
                <label htmlFor="item-name">Item Name *</label>
                <Input
                  id="item-name"
                  ref={nameInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter item name"
                  required
                />
              </InputGroup>
              <InputGroup>
                <label htmlFor="current-stock">Current Stock *</label>
                <Input
                  id="current-stock"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.currentStock}
                  onChange={(e) => setFormData({ ...formData, currentStock: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </InputGroup>
              <InputGroup>
                <label htmlFor="unit-select">Unit *</label>
                <UnitSelect
                  id="unit-select"
                  value={unitOptions.includes(formData.unit) ? formData.unit : (formData.unit ? 'custom' : '')}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setFormData({ ...formData, unit: '' });
                    } else if (e.target.value) {
                      setFormData({ ...formData, unit: e.target.value });
                    } else {
                      setFormData({ ...formData, unit: '' });
                    }
                  }}
                >
                  <UnitOption value="">Select a unit</UnitOption>
                  {unitOptions.map((unit) => (
                    <UnitOption key={unit} value={unit}>
                      {unit}
                    </UnitOption>
                  ))}
                  <UnitOption value="custom">Other (Type below)</UnitOption>
                </UnitSelect>
                {(!formData.unit || !unitOptions.includes(formData.unit)) && (
                  <Input
                    id="unit-custom"
                    type="text"
                    value={formData.unit || ''}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="Enter custom unit (e.g., pieces, boxes)"
                    style={{ marginTop: theme.spacing.xs }}
                  />
                )}
              </InputGroup>
              <InputGroup>
                <label htmlFor="minimum-stock">Minimum Stock *</label>
                <Input
                  id="minimum-stock"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.minimumStock}
                  onChange={(e) => setFormData({ ...formData, minimumStock: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </InputGroup>
              <ButtonGroup>
                <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  {editingItem ? 'Update' : 'Add'} Item
                </Button>
              </ButtonGroup>
            </ModalForm>
          </ModalContent>
        </Modal>
      )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default InventoryManagement;

